/**
 * @file Listens to one report's `runs` under the query the caller passes and
 * returns each run with its document id. Consumers: the Reports list's LAST
 * RUN cell (newest one) and `useAnalyticsReportArchive`. The store
 * comes from the keyed registry, so equal reads share one listener; with no
 * report it is the inert `__inactive__` store, which opens none and is never
 * loading. The caller keeps `queryOptions` stable across renders. A failed
 * read returns its message as `error`, kept beside the store it came from, so
 * a new report never shows an old report's failure.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import { createCollectionStore } from '@/store';
import type { QueryOptions } from '@/store';
import { ANALYTICS_REPORT_RUNS_COLLECTION, analyticsReportPath } from '#types';
import type { AnalyticsReportRun } from '#types';

import type { AnalyticsArchivedRun } from './analyticsReportsTab.types';

const INACTIVE_REPORT_ID = '__inactive__';

export const useAnalyticsReportRuns = (
  reportId: string | null,
  queryOptions: QueryOptions,
): { runs: AnalyticsArchivedRun[]; loading: boolean; error: string | null } => {
  const store = useMemo(
    () =>
      createCollectionStore<AnalyticsReportRun>(
        `${analyticsReportPath(reportId ?? INACTIVE_REPORT_ID)}/${ANALYTICS_REPORT_RUNS_COLLECTION}`,
        { queryOptions },
      ),
    [reportId, queryOptions],
  );
  const [failure, setFailure] = useState<{
    store: typeof store;
    message: string;
  } | null>(null);

  useEffect(() => {
    void store.connect({
      onError: (error) => setFailure({ store, message: error.message }),
    });
    return () => store.disconnect();
  }, [store]);

  const snapshot = useSnapshot(store.state);
  return {
    runs: snapshot.docIds.flatMap((runId) => {
      const run = snapshot.docs.get(runId)?.data;
      return run ? [{ runId, run }] : [];
    }),
    loading: snapshot.loading,
    error: failure !== null && failure.store === store ? failure.message : null,
  };
};
